import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { get } from "@/api/client";
import { Wordmark } from "@/brand/Logo";

interface VerifyResult {
  found: boolean;
  valid: boolean;
  status: "" | "valid" | "expired" | "revoked";
  signature_ok: boolean;
  credential_id: string;
  track: string;
  track_name: string;
  holder_name: string;
  org_name: string;
  issued_at: string;
  expires_at: string;
}

/**
 * Public, no sign-in: anyone with a credential id (or the QR on a certificate)
 * can confirm a MultiDoc credential here. It shows only the display facts and
 * whether the vendor signature checks out — nothing else about the holder.
 */
export default function VerifyPage() {
  const { credentialId = "" } = useParams();
  const [state, setState] = useState<VerifyResult | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    setState(null);
    setError(false);
    get<VerifyResult>(`/public/certifications/${encodeURIComponent(credentialId)}`)
      .then((d) => { if (live) setState(d); })
      .catch(() => { if (live) setError(true); });
    return () => { live = false; };
  }, [credentialId]);

  const file = `/api/public/certifications/${encodeURIComponent(credentialId)}/certificate`;
  return (
    // Its own frame, not the sign-in one: the certificate needs the width, and every
    // surface takes the theme's colours so it reads in light and dark mode alike.
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", color: "text.primary" }}>
      <Box component="header" sx={{ bgcolor: "header.main", color: "header.contrastText",
                                   px: { xs: 2, sm: 4 }, py: 2, display: "flex",
                                   alignItems: "center", gap: 1.25 }}>
        <Wordmark onBrand height={26} />
        <Typography component="span" sx={{ color: "inherit" }}>Certification</Typography>
      </Box>

      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 4 }, py: { xs: 3, md: 5 } }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>Verify a certification</Typography>

        {error ? (
          <Alert severity="error">Could not check that credential. Please try again.</Alert>
        ) : !state ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
        ) : !state.found ? (
          <Alert severity="warning">
            No credential found for <code>{credentialId}</code>. Check the id and try again.
          </Alert>
        ) : (
          <Box sx={{ display: "grid", gap: 3, alignItems: "start",
                     gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(280px, 1fr)" } }}>
            <Box sx={{ order: { xs: 2, md: 1 } }}>
              <Box component="a" href={file} target="_blank" rel="noopener noreferrer"
                   title="Open the certificate full size" sx={{ display: "block" }}>
                <Box component="img" src={file} alt={`Certificate ${state.credential_id}`}
                     sx={{ display: "block", width: "100%", height: "auto", borderRadius: 1,
                           border: 1, borderColor: "divider", boxShadow: 3 }} />
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button variant="outlined" size="small" href={`${file}?format=png`}
                        download={`${state.credential_id}.png`}>Download PNG</Button>
                <Button size="small" href={file} target="_blank" rel="noopener noreferrer">
                  Open full size
                </Button>
              </Stack>
            </Box>

            <Stack spacing={2} sx={{ order: { xs: 1, md: 2 } }}>
              <Banner valid={state.valid} status={state.status} signatureOk={state.signature_ok} />
              <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Detail label="Holder" value={state.holder_name} strong />
                <Detail label="Credential" value={`${state.track_name} (${state.track})`} />
                <Detail label="Organisation" value={state.org_name || "—"} />
                <Detail label="ID" value={state.credential_id} mono />
                <Detail label="Issued" value={fmt(state.issued_at)} />
                <Detail label="Expires" value={state.expires_at ? fmt(state.expires_at) : "No expiry"} />
              </Paper>
              <Typography variant="caption" color="text.secondary">
                Checked against MultiDoc&apos;s signing key just now. Only the facts above are
                published; nothing else about the holder.
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function Banner({ valid, status, signatureOk }: {
  valid: boolean; status: string; signatureOk: boolean;
}) {
  if (valid) {
    return (
      <Alert severity="success" icon={false}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" color="success" label="Valid" />
          <Typography variant="body2">This is a genuine, current MultiDoc credential.</Typography>
        </Stack>
      </Alert>
    );
  }
  if (!signatureOk) {
    return <Alert severity="error">The signature on this credential does not verify.</Alert>;
  }
  if (status === "revoked") return <Alert severity="error">This credential has been revoked.</Alert>;
  if (status === "expired") return <Alert severity="warning">This credential has expired.</Alert>;
  return <Alert severity="warning">This credential is not currently valid.</Alert>;
}

function fmt(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}

function Detail({ label, value, mono = false, strong = false }: {
  label: string; value: string; mono?: boolean; strong?: boolean;
}) {
  return (
    <Stack spacing={0.25} sx={{ py: 0.75 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}
                  sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</Typography>
      <Typography variant={strong ? "h6" : "body1"} color="text.primary"
                  component={mono ? "code" : "span"}
                  sx={{ minWidth: 0, overflowWrap: "anywhere",
                        ...(mono && { fontFamily: "monospace" }) }}>
        {value}
      </Typography>
    </Stack>
  );
}
